import React, { useEffect, useState } from 'react'
import { BarChartOutlined, CaretRightOutlined, MailOutlined } from '@ant-design/icons'
import { Alert, Tag } from 'antd'
import API, { API_VERSION, REQUEST_METHOD } from '../../api'
import Loader from '../../common/layout/components/Loader'

const RankingInformationTab = ({ order: { id: orderId } }) => {
  const [ranking, setRanking] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  useEffect(() => {
    const fetchRankingData = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await API(
          `order/${orderId}/ranking-information`,
          API_VERSION.TWO
        ).request(REQUEST_METHOD.GET, {})
        setRanking(response)
      } catch (err) {
        setError(err._error || err.error || err.message || 'Failed to fetch ranking information')
      } finally {
        setLoading(false)
      }
    }
    if (orderId) {
      fetchRankingData()
    }
  }, [orderId])

  if (loading) {
    return <Loader />
  }

  const panelists = ranking?.panelists || []

  return (
    <div style={{ padding: '16px' }}>
      <h4 className="lead">
        <BarChartOutlined /> Ranking Information
      </h4>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      {!error && ranking && (
        <>
          <p style={{ color: '#8c8c8c', marginBottom: '16px' }}>
            {ranking.ranking_type === 'round_robin' && <Tag color="blue">Round Robin</Tag>}
            {panelists.length} Panelist{panelists.length !== 1 ? 's' : ''}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {/* Header Row */}
            <div style={{
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#fafafa',
              border: '1px solid #f0f0f0',
              borderBottom: 'none',
              fontSize: '12px',
              fontWeight: 600,
              color: '#595959',
            }}>
              <div style={{ width: 60 }}>Rank</div>
              <div style={{ flex: 2 }}>Name</div>
              <div style={{ flex: 2 }}>Most Recent Order Date</div>
              <div style={{ flex: 2 }}>Email</div>
              <div style={{ flex: 1 }}></div>
            </div>

            {panelists.map((panelist, index) => {
              const isExpanded = expandedId === panelist.panelist_id
              const hasRecentOrder = !!(
                panelist.order_loan_number ||
                panelist.order_loan_address ||
                panelist.product_name ||
                (panelist.products && panelist.products.length > 0)
              )
              const mostRecentOrderDate = panelist.created_date
                ? new Date(panelist.created_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                : null

              return (
                <div
                  key={panelist.panelist_id}
                  data-panelist-id={panelist.panelist_id}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderTop: index === 0 ? 'none' : '1px solid #f0f0f0',
                    overflow: 'visible',
                    backgroundColor: isExpanded ? '#fff' : '#fff',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Data Row */}
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : panelist.panelist_id)}
                    style={{
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'background 0.3s',
                      backgroundColor: isExpanded ? '#fff' : '#fff',
                    }}
                    onMouseEnter={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = '#fafafa')}
                    onMouseLeave={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = '#fff')}
                  >
                    <div style={{ width: 60, color: '#595959', fontWeight: 'bold' }}>
                      #{index + 1}
                    </div>

                    {/* Fallback if panelist name is not available */}
                    <div style={{ flex: 2 }}>
                      <div style={{ fontWeight: 600, color: '#262626' }}>
                        {panelist.panelist_name || 'Appraiser'}
                      </div>
                    </div>

                    <div style={{ flex: 2, color: '#595959' }}>
                      {mostRecentOrderDate || 'N/A'}
                    </div>

                    <div style={{ flex: 2 }}>
                      {panelist.panelist_email && (
                        <span style={{ fontSize: '13px', color: '#1890ff' }}>
                          <MailOutlined /> {panelist.panelist_email}
                        </span>
                      )}
                    </div>

                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <CaretRightOutlined rotate={isExpanded ? 90 : 0} style={{ color: '#bfbfbf' }} />
                    </div>
                  </div>

                  {/* Expandable Details */}
                  {isExpanded && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#f5f5f5',
                      borderTop: '1px solid #f0f0f0',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {hasRecentOrder ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                            <div style={{ width: 60 }}></div>
                            <div style={{ flex: 2 }}>
                              <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 'bold', marginBottom: '4px' }}>LOAN NUMBER</div>
                              <div style={{ fontSize: '13px', color: '#262626' }}>
                                {panelist.order_loan_number || 'N/A'}
                              </div>
                            </div>
                            <div style={{ flex: 2 }}>
                              <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 'bold', marginBottom: '4px' }}>PRODUCT</div>
                              <div style={{ fontSize: '13px', color: '#262626' }}>
                                {panelist.products && panelist.products.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {panelist.products.map((product, idx) => (
                                      <div key={product.id || idx}>
                                        {product.description || 'N/A'}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  panelist.product_name || 'N/A'
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 2 }}>
                              <div style={{ fontSize: '11px', color: '#8c8c8c', fontWeight: 'bold', marginBottom: '4px' }}>ADDRESS</div>
                              <div style={{ fontSize: '13px', color: '#262626' }}>
                                {panelist.order_loan_address || 'N/A'}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}></div>
                          </div>
                          <div style={{ fontSize: '11px', color: '#8c8c8c', fontStyle: 'italic' }}>
                            Most Recent Order Details
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#8c8c8c', fontStyle: 'italic' }}>
                          No recent orders
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default RankingInformationTab
